import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TaskListView from '../../components/common/TaskListView';
import InspectionDetailModal from '../../components/common/InspectionDetailModal';
import projectService from '../../services/projectService';
import inspectionService from '../../services/inspectionService';
import { useAuth } from '../../contexts/AuthContext';
import { formatCategory, getTrackedCategoryOptions } from './categoryOptions';
import '../monitoring/Monitoring.css';

/**
 * Inspections is the "assigned-worker view" of Monitoring: it lists the same
 * unified tasks as Monitoring > Tasks (EIA-condition tasks plus tasks
 * derived from assigned MonitoringParameters), scoped server-side to the
 * current user unless they have manager/admin oversight.
 *
 * On top of that, "New Inspection" starts a full checklist-based site
 * inspection (TaskExecution) against a project + category. The checklist is
 * pulled from that project's active MonitoringParameters, so it stays
 * org-configured rather than hardcoded.
 */
export const NewInspectionModal = ({ onClose, initialProjectId = '', initialCategory = '' }) => {
  const navigate = useNavigate();
  const { organisation } = useAuth();

  // Only offer categories this org actually tracks (plus its custom ones) -
  // a caller-supplied initialCategory (e.g. from a Category Overview page's
  // quick action) is always kept selectable even if it's no longer tracked.
  const categoryOptions = useMemo(() => (
    getTrackedCategoryOptions(organisation?.trackingCategories, organisation?.customCategories || [], initialCategory || null)
  ), [organisation, initialCategory]);

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(initialProjectId);
  const [category, setCategory] = useState(initialCategory || categoryOptions[0]?.value || 'osh');

  useEffect(() => {
    (async () => {
      try {
        const data = await projectService.getAll();
        setProjects(data.projects || []);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
        toast.error('Failed to load projects');
      }
    })();
  }, []);

  const handleStart = () => {
    if (!projectId) {
      toast.error('Please select a project');
      return;
    }
    navigate(`/inspections/new/execute?projectId=${projectId}&category=${category}`);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="monitoring-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><i className="fas fa-clipboard-check"></i> New Inspection</h2>
          <button className="close-btn" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>
        <div className="modal-content">
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Project *</label>
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">Select a project...</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group full-width">
              <label>Category *</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {categoryOptions.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary-green" onClick={handleStart}>Start Inspection</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Real, submitted Inspection records (checklist-based site inspections),
 * separate from the pending/derived "tasks" TaskListView shows above. This
 * is what lets someone click through and see what an inspection actually
 * found, rather than only knowing a task was marked done.
 */
const RecentInspections = ({ onView }) => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInspections = useCallback(async () => {
    try {
      setLoading(true);
      const data = await inspectionService.getAll();
      setInspections(data.inspections || []);
    } catch (error) {
      console.error('Failed to fetch inspections:', error);
      toast.error(error.response?.data?.error || 'Failed to load inspections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInspections(); }, [fetchInspections]);

  return (
    <div className="monitoring-page" style={{ paddingTop: 0 }}>
      <div className="monitoring-header">
        <div>
          <h2 style={{ margin: 0, fontSize: '1.15rem' }}><i className="fas fa-clipboard-list"></i> Recent Inspections</h2>
          <p className="header-subtitle">Submitted checklist inspections - click one to see full details</p>
        </div>
      </div>

      {loading ? (
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading inspections...</h3>
        </div>
      ) : inspections.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-clipboard-list"></i>
          <h3>No inspections submitted yet</h3>
          <p>Use "New Inspection" above to conduct and submit your first one.</p>
        </div>
      ) : (
        <div className="monitoring-list">
          {inspections.map((inspection) => (
            <div key={inspection.id} className="monitoring-card" style={{ cursor: 'pointer' }} onClick={() => onView(inspection.id)}>
              <div className="monitoring-card-main">
                <h4>{formatCategory(inspection.category)} Inspection{inspection.location ? ` - ${inspection.location}` : ''}</h4>
                <div className="monitoring-card-meta">
                  <span><i className="fas fa-project-diagram"></i> {inspection.project?.name || 'Project'}</span>
                  <span><i className="fas fa-calendar"></i> {new Date(inspection.submittedAt).toLocaleDateString()}</span>
                  <span><i className="fas fa-percent"></i> {inspection.completionPercentage}% complete</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span className={`status-badge status-${inspection.overallCompliance}`}>{inspection.overallCompliance.replace(/_/g, ' ')}</span>
                <button className="btn-icon" title="View details" onClick={(e) => { e.stopPropagation(); onView(inspection.id); }}>
                  <i className="fas fa-eye"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Inspections = () => {
  const [showNewInspection, setShowNewInspection] = useState(false);
  const [viewingInspectionId, setViewingInspectionId] = useState(null);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1.5rem 1.5rem 0' }}>
        <Link to="/monitoring/parameters/new" className="btn-secondary">
          <i className="fas fa-sliders-h"></i> Configure a Monitoring Parameter
        </Link>
        <button className="btn-primary-green" onClick={() => setShowNewInspection(true)}>
          <i className="fas fa-plus"></i> New Inspection
        </button>
      </div>
      <TaskListView
        title="Inspections"
        subtitle="Track monitoring tasks and inspection records assigned to you"
      />
      <RecentInspections onView={setViewingInspectionId} />
      {showNewInspection && <NewInspectionModal onClose={() => setShowNewInspection(false)} />}
      <InspectionDetailModal inspectionId={viewingInspectionId} onClose={() => setViewingInspectionId(null)} />
    </div>
  );
};

export default Inspections;
